// src/components/Events/NewYear/hooks/useMatrixCanvas.js
import { useCallback, useEffect, useRef } from "react";

/* ===============================
   🎨 신년 네온 팔레트 (상수)
================================ */

const pickColor = () => {
    const huePool = [165, 190, 205, 255, 285, 315];
    const h = huePool[Math.floor(Math.random() * huePool.length)];
    const s = 78 + Math.random() * 12; // 78~90%
    const l = 60 + Math.random() * 8;
    return `hsl(${h}, ${s}%, ${l}%)`;
};

const AMBIENT_TEXTS = ["2026", "♥", "★"];
const pickAmbientText = () =>
    AMBIENT_TEXTS[Math.floor(Math.random() * AMBIENT_TEXTS.length)];

const estimateTextWidth = (text, fontSize) => {
    const s = String(text ?? "");
    return Math.max(8, s.length * fontSize * 0.62);
};

const pickXForText = (w, text, fontSize, pad) => {
    const est = estimateTextWidth(text, fontSize);
    const maxX = Math.max(pad, w - pad - est);
    const minX = pad;
    if (maxX <= minX) return minX;
    return minX + Math.random() * (maxX - minX);
};

/* ===============================
   ✅ 모듈 상단 상수
================================ */

const PUSH_WINDOW_MS = 12_000;
const SPAWN_RATE_WINDOW_MS = 1000;

// WS(실제 채팅) 스폰 제한
const PC_WS_SPAWN_PER_SEC_NORMAL = 14;
const PC_WS_SPAWN_PER_SEC_BUSY = 6;

// LOOP(무한레인) 스폰 제한 — busy여도 “완전 0”은 안 되게
const PC_LOOP_SPAWN_PER_SEC_NORMAL = 9; // 140ms면 7.1/s → 여유
const PC_LOOP_SPAWN_PER_SEC_BUSY = 6;   // busy에서도 140ms(7.1/s)보단 낮으니 일부만 스킵되지만 “계속” 나옴

export default function useMatrixCanvas(canvasRef, options = {}) {
    const { isMobile = false } = options;

    const dropsRef = useRef([]);
    const rafRef = useRef(0);
    const initedRef = useRef(false);

    const sizeRef = useRef({ w: 1, h: 1 });

    const lastFrameRef = useRef(0);
    const lastStepRef = useRef(0);

    // ✅ busy 판단은 “실제 채팅(WS)”만으로
    const recentWsPushesRef = useRef([]);

    // ✅ 스폰 제한도 WS/LOOP 분리
    const wsSpawnTimesRef = useRef([]);
    const loopSpawnTimesRef = useRef([]);

    const busyRef = useRef(false);

    /* ===============================
       🎯 성능 튜닝
    ============================== */

    const MAX_DROPS = isMobile ? 55 : 48;
    const SPAWN_EVERY_BASE = isMobile ? 44 : 46;

    const SHADOW_BLUR = isMobile ? 3 : 6;

    const FONT_BASE = isMobile ? 16 : 18;
    const FONT_RAND = isMobile ? 6 : 8;
    const BIG_FONT_BASE = isMobile ? 20 : 26;
    const BIG_FONT_RAND = isMobile ? 6 : 6;

    const PAD_AMBIENT = isMobile ? 26 : 18;
    const PAD_PUSH = isMobile ? 30 : 20;

    const BUSY_THRESHOLD = isMobile ? 45 : 55;
    const DRAW_LIMIT_BUSY = isMobile ? 50 : 38;

    /**
     * opt.source:
     * - "ws": 실제 채팅(웹소켓)
     * - "loop": 무한레인(옵션B)
     * - "ambient": 자동 ambient
     */
    const pushMatrixText = useCallback(
        (text, opt = {}) => {
            const canvas = canvasRef.current;
            if (!canvas) return;

            const now = Date.now();
            const source = opt.source || "ws";

            // ✅ 1) busy 판단은 ws만 기록
            if (source === "ws") {
                const arr = recentWsPushesRef.current;
                arr.push(now);
                while (arr.length && now - arr[0] > PUSH_WINDOW_MS) arr.shift();
            }

            // ✅ 2) PC 스폰 제한(WS/LOOP 분리)
            if (!isMobile) {
                const isBusyNow = recentWsPushesRef.current.length >= BUSY_THRESHOLD;

                if (source === "ws") {
                    const sArr = wsSpawnTimesRef.current;
                    sArr.push(now);
                    while (sArr.length && now - sArr[0] > SPAWN_RATE_WINDOW_MS) sArr.shift();

                    const cap = isBusyNow ? PC_WS_SPAWN_PER_SEC_BUSY : PC_WS_SPAWN_PER_SEC_NORMAL;
                    if (sArr.length > cap) return;

                    // ws 폭주 상태면 라이트로 강등
                    if (isBusyNow && opt.forceFull !== true) {
                        opt = {
                            ...opt,
                            fontSize: Math.min(
                                opt.fontSize ?? (BIG_FONT_BASE + Math.random() * BIG_FONT_RAND),
                                18
                            ),
                            opacity: Math.min(opt.opacity ?? 0.28, 0.28),
                            lite: true,
                        };
                    }
                }

                if (source === "loop") {
                    const sArr = loopSpawnTimesRef.current;
                    sArr.push(now);
                    while (sArr.length && now - sArr[0] > SPAWN_RATE_WINDOW_MS) sArr.shift();

                    const cap = isBusyNow ? PC_LOOP_SPAWN_PER_SEC_BUSY : PC_LOOP_SPAWN_PER_SEC_NORMAL;
                    if (sArr.length > cap) return;

                    // loop는 원래도 가볍게
                    opt = {
                        ...opt,
                        lite: true,
                        opacity: Math.min(opt.opacity ?? 0.12, 0.18),
                        fontSize: Math.min(opt.fontSize ?? 18, 20),
                    };
                }
            }

            const w = sizeRef.current.w || canvas.getBoundingClientRect().width || 1;
            const fontSize = opt.fontSize ?? (BIG_FONT_BASE + Math.random() * BIG_FONT_RAND);
            const x = pickXForText(w, text, fontSize, PAD_PUSH);

            dropsRef.current.push({
                x,
                y: -Math.max(30, fontSize + 8),
                text,
                speed:
                    opt.speed ??
                    ((isMobile ? 1.35 : 1.0) + Math.random() * (isMobile ? 1.15 : 1.0)),
                fontSize,
                opacity: opt.opacity ?? 1,
                color: opt.color ?? pickColor(),
                isAmbient: false,
                lite: !!opt.lite,
            });

            if (dropsRef.current.length > MAX_DROPS) {
                dropsRef.current.splice(0, dropsRef.current.length - MAX_DROPS);
            }
        },
        [canvasRef, isMobile, MAX_DROPS, BIG_FONT_BASE, BIG_FONT_RAND, PAD_PUSH, BUSY_THRESHOLD]
    );

    useEffect(() => {
        let cancelled = false;
        let removeResize = null;
        let ro = null;

        const initWhenReady = () => {
            if (cancelled) return;

            const canvas = canvasRef.current;
            if (!canvas) {
                rafRef.current = requestAnimationFrame(initWhenReady);
                return;
            }

            const ctx = canvas.getContext("2d");
            if (!ctx) return;

            if (initedRef.current) return;
            initedRef.current = true;

            const resize = () => {
                const raw = window.devicePixelRatio || 1;
                const dprCap = !isMobile && busyRef.current ? 1.5 : isMobile ? 1.5 : 2;
                const dpr = Math.min(raw, dprCap);

                const rect = canvas.getBoundingClientRect();
                sizeRef.current = { w: rect.width || 1, h: rect.height || 1 };

                canvas.width = Math.floor((rect.width || 1) * dpr);
                canvas.height = Math.floor((rect.height || 1) * dpr);
                ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            };

            resize();
            window.addEventListener("resize", resize);
            removeResize = () => window.removeEventListener("resize", resize);

            if (typeof ResizeObserver !== "undefined") {
                ro = new ResizeObserver(() => resize());
                ro.observe(canvas);
            }

            const spawnAmbientDrop = () => {
                const w = sizeRef.current.w || 1;
                const fontSize = FONT_BASE + Math.random() * FONT_RAND;
                const text = pickAmbientText();
                const x = pickXForText(w, text, fontSize, PAD_AMBIENT);

                dropsRef.current.push({
                    x,
                    y: -Math.max(40, fontSize + 10),
                    text,
                    speed:
                        (isMobile ? 1.25 : 1.0) + Math.random() * (isMobile ? 1.05 : 0.9),
                    fontSize,
                    opacity: isMobile ? 0.26 : 0.20,
                    color: pickColor(),
                    isAmbient: true,
                    lite: true,
                });

                if (dropsRef.current.length > MAX_DROPS) {
                    dropsRef.current.splice(0, dropsRef.current.length - MAX_DROPS);
                }
            };

            let ambientTick = 0;

            const animate = (t) => {
                if (cancelled) return;

                const FPS = isMobile ? 30 : 60;
                const frameGap = 1000 / FPS;
                const last = lastFrameRef.current || 0;

                if (t - last < frameGap) {
                    rafRef.current = requestAnimationFrame(animate);
                    return;
                }
                lastFrameRef.current = t;

                const prev = lastStepRef.current || t;
                const dt = Math.min(3, Math.max(0.5, (t - prev) / 16.67));
                lastStepRef.current = t;

                const { w, h } = sizeRef.current;
                ctx.clearRect(0, 0, w, h);

                // ✅ ws push window 정리 + busy 판정(WS 기반)
                const now = Date.now();
                const arr = recentWsPushesRef.current;
                while (arr.length && now - arr[0] > PUSH_WINDOW_MS) arr.shift();

                const n = arr.length;
                const isBusy = n >= BUSY_THRESHOLD;
                if (busyRef.current !== isBusy) {
                    busyRef.current = isBusy;
                    resize();
                }

                const spawnEveryDynamic = Math.max(
                    isMobile ? 40 : isBusy ? 80 : 46,
                    SPAWN_EVERY_BASE + n * 3
                );

                ambientTick++;
                if (ambientTick % spawnEveryDynamic === 0) spawnAmbientDrop();

                const allDrops = dropsRef.current;

                const drawDrops =
                    !isMobile && isBusy && allDrops.length > DRAW_LIMIT_BUSY
                        ? allDrops.slice(-DRAW_LIMIT_BUSY)
                        : allDrops;

                for (let i = 0; i < drawDrops.length; i++) {
                    const d = drawDrops[i];

                    const boost = isMobile ? 1.25 : 1.0;
                    const baseAlpha = (d.opacity ?? 1) * boost;

                    const minAlpha = d.isAmbient ? 0.16 : 0.28;
                    ctx.globalAlpha = Math.max(minAlpha, Math.min(1, baseAlpha));

                    ctx.font = `700 ${d.fontSize ?? 18}px "Inter","Apple SD Gothic Neo","Pretendard",sans-serif`;

                    ctx.shadowBlur = 0;
                    ctx.lineWidth = Math.max(
                        1.8,
                        d.fontSize * (isMobile ? 0.26 : busyRef.current ? 0.18 : 0.22)
                    );
                    ctx.strokeStyle = "rgba(0,0,0,0.92)";
                    ctx.strokeText(d.text, d.x, d.y);

                    const allowGlow = isMobile ? true : !busyRef.current && !d.lite;
                    if (!d.isAmbient && allowGlow) {
                        ctx.shadowColor = d.color ?? "#fff";
                        ctx.shadowBlur = 6;
                        ctx.lineWidth = Math.max(1, d.fontSize * 0.06);
                        ctx.strokeStyle = "rgba(255,255,255,0.18)";
                        ctx.strokeText(d.text, d.x, d.y);
                    }

                    if (d.isAmbient || d.lite || busyRef.current) {
                        ctx.shadowBlur = 0;
                    } else {
                        ctx.shadowBlur = SHADOW_BLUR;
                        ctx.shadowColor = d.color ?? "#fff";
                    }

                    ctx.fillStyle = d.color ?? "#fff";
                    ctx.fillText(d.text, d.x, d.y);
                }

                // ✅ 위치 업데이트는 전체 drops 기준
                for (let i = 0; i < allDrops.length; i++) {
                    const d = allDrops[i];
                    d.y += (d.speed ?? 2.5) * dt;
                }

                ctx.shadowBlur = 0;
                ctx.globalAlpha = 1;

                dropsRef.current = allDrops.filter((d) => d.y < h + 120);

                rafRef.current = requestAnimationFrame(animate);
            };

            rafRef.current = requestAnimationFrame(animate);
        };

        initWhenReady();

        return () => {
            cancelled = true;
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            if (removeResize) removeResize();
            if (ro) ro.disconnect();
            initedRef.current = false;
            lastFrameRef.current = 0;
            lastStepRef.current = 0;
        };
    }, [
        canvasRef,
        isMobile,
        MAX_DROPS,
        SHADOW_BLUR,
        FONT_BASE,
        FONT_RAND,
        SPAWN_EVERY_BASE,
        PAD_AMBIENT,
        BUSY_THRESHOLD,
        DRAW_LIMIT_BUSY,
    ]);

    return { pushMatrixText };
}
